import * as XLSX from 'xlsx';

export interface ExcelColumn<T> {
  /** Header afișat pe primul rând al foii. */
  header: string;
  /** Cum extragem valoarea unei celule din rând. */
  value: (row: T) => string | number | Date | null | undefined;
  /** Lățime aproximativă (caractere). Folosită pentru `wch` (width-in-chars). */
  width?: number;
}

export interface ExportXlsxOptions<T> {
  /** Numele fișierului fără extensie (extensia `.xlsx` se adaugă automat). */
  fileName: string;
  /** Numele foii. Default `Sheet1`. */
  sheetName?: string;
  columns: ExcelColumn<T>[];
  rows: T[];
}

/**
 * Generează și descarcă un `.xlsx` din lista de rânduri primită, folosind
 * coloanele declarate (cu un mic helper pentru lățime). Datele de tip `Date`
 * sunt scrise ca date Excel reale (nu string).
 *
 * Notă: pentru fișiere foarte mari (mii de rânduri) Excel rămâne ok — `xlsx`
 * scrie un workbook simplu fără să încarce memorie suplimentară semnificativă.
 */
export function exportXlsx<T>({ fileName, sheetName = 'Sheet1', columns, rows }: ExportXlsxOptions<T>): void {
  // Matricea finală: header + valorile randate.
  const header = columns.map((c) => c.header);
  const body = rows.map((row) => columns.map((c) => normalizeCell(c.value(row))));
  const aoa = [header, ...body];

  const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });

  // Lățimi de coloane. Dacă nu sunt setate, lăsăm fallback bazat pe header.
  ws['!cols'] = columns.map((c) => ({
    wch: c.width != null ? c.width : Math.max(10, c.header.length + 2),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31)); // Excel cap 31

  const final = sanitizeFileName(fileName) || 'export';
  XLSX.writeFile(wb, `${final}.xlsx`);
}

/**
 * Normalizează celula la un tip simplu acceptat de `xlsx`. `null`/`undefined`
 * devin string gol; obiectele rămase devin string prin `String()`.
 */
function normalizeCell(v: unknown): string | number | Date {
  if (v == null) return '';
  if (v instanceof Date) return v;
  if (typeof v === 'number' || typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  return String(v);
}

/** Curăță caracterele problematice din numele fișierului. */
function sanitizeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '_').trim();
}
